import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";

const AFFILIATIONS = [
  { kind: "Backed by", name: "IIM-FIELD" },
  { kind: "Supported by", name: "SISFS" },
  { kind: "Incubated at", name: "JITO-JIIF" },
];

const BROKERS = [
  { name: "Zerodha", color: "#387ED1", x: "5%", y: "18%", rot: "-6deg", size: "text-2xl" },
  { name: "Angel One", color: "#F26522", x: "78%", y: "12%", rot: "4deg", size: "text-xl" },
  { name: "Groww", color: "#00B386", x: "16%", y: "70%", rot: "-3deg", size: "text-2xl" },
  { name: "Dhan", color: "#0F2A5A", x: "62%", y: "74%", rot: "5deg", size: "text-xl" },
  { name: "Motilal Oswal", color: "#C8102E", x: "40%", y: "20%", rot: "-2deg", size: "text-base" },
  { name: "Upstox", color: "#410DAA", x: "82%", y: "55%", rot: "3deg", size: "text-xl" },
  { name: "5paisa", color: "#FFB800", x: "30%", y: "55%", rot: "2deg", size: "text-base" },
];

export default function Backers() {
  // Repeat enough for a smooth, gap-less right→left marquee
  const loop = [...AFFILIATIONS, ...AFFILIATIONS, ...AFFILIATIONS, ...AFFILIATIONS, ...AFFILIATIONS, ...AFFILIATIONS];

  return (
    <section
      id="backers"
      data-testid="backers-section"
      className="relative py-20 md:py-28 bg-white border-y border-[#e2e8f0] overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-semibold text-[#084392] font-mono tracking-[0.2em]">
            <Sparkles size={12} />
            TRUSTED FOUNDATIONS
          </div>
          <h2 className="font-heading mt-5 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1]">
            Built on solid ground.
          </h2>
          <p className="mt-4 text-lg text-[#475569]">
            Tradecure is incubated, supported and mentored by some of India&rsquo;s most
            credible institutions.
          </p>
        </motion.div>

        {/* Edge-to-edge inline marquee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative mt-12 -mx-5 md:-mx-8"
        >
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          <div
            className="flex ticker-rev gap-12 md:gap-16 whitespace-nowrap py-4"
            data-testid="affiliations-marquee"
          >
            {loop.map((a, i) => (
              <span key={i} className="inline-flex items-center gap-3 shrink-0">
                <span className="font-heading text-2xl md:text-3xl lg:text-4xl tracking-tight">
                  <span className="font-normal text-[#475569]">{a.kind} </span>
                  <span className="font-black text-[#084392]">{a.name}</span>
                </span>
                <span className="font-mono text-2xl md:text-3xl text-[#cbd5e1]">✦</span>
              </span>
            ))}
          </div>
        </motion.div>

        {/* Brokers reveal box with scattered watermarks INSIDE */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="relative mt-12 rounded-2xl border border-[#084392]/20 bg-white overflow-hidden"
          data-testid="brokers-banner"
        >
          {/* Scattered broker watermarks — inside the box */}
          <div className="absolute inset-0 pointer-events-none select-none">
            {BROKERS.map((b, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 0.22 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, delay: i * 0.08 }}
                className={`absolute font-heading font-black tracking-tight ${b.size} hidden md:block`}
                style={{
                  left: b.x,
                  top: b.y,
                  color: b.color,
                  transform: `rotate(${b.rot})`,
                  animation: `float-slow ${10 + i * 2}s ease-in-out infinite`,
                  animationDelay: `${i * 0.7}s`,
                }}
                data-testid={`broker-watermark-${b.name.toLowerCase().replace(" ", "-")}`}
              >
                {b.name}
              </motion.div>
            ))}
          </div>

          {/* Foreground content */}
          <div className="relative p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 min-h-[280px]">
            <div className="bg-white/85 backdrop-blur-sm rounded-xl p-5 md:p-6 max-w-xl">
              <div className="flex items-center gap-2 font-mono text-xs font-bold text-[#084392] tracking-[0.2em]">
                <span className="h-2 w-2 rounded-full bg-[#084392] pulse-dot" />
                BROKERS SUPPORTED
              </div>
              <h3 className="font-heading mt-3 text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight">
                Revealing soon. Stay tuned.
              </h3>
              <p className="mt-2 text-[#475569]">
                We&rsquo;re finalising integrations with India&rsquo;s most-loved discount
                and full-service brokers. Waitlist members get the first peek.
              </p>
            </div>
            <a
              href="#waitlist"
              data-testid="brokers-banner-cta"
              className="relative shrink-0 inline-flex items-center gap-2 rounded-md bg-[#084392] hover:bg-[#06326e] text-white font-semibold h-12 px-6 transition"
            >
              Get notified
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
